import { createAction } from 'redux-actions';
import { push } from 'connected-react-router';
import get from 'lodash/get';

import { subscriptionStatus, userProductId } from '@app/constants/subscribe';
import RouterConstants from '@app/constants/router';
import { mapUserOptions, getSubscriptionActiveStatus } from '@app/helpers/subscriptionHelper';
import { processPayment } from '@app/actions/paymentActions';
import { userLogout, notifyUserNotSupported } from '@app/actions/userActions';
import { getAlphaLangCode } from '@app/selectors/localiztionSelectors';
import * as SubscribeService from '@app/services/subscribeService';
import { invoicesList } from '@app/services/accountService';

export const resetSubscriptionErrors = createAction('SUBSCRIBE/RESET_SUBSCRIPTION_ERRORS');
export const successAddUserOption = createAction('SUBSCRIBE/SUCCESS_ADD_USER_OPTION');
export const failureAddUserOption = createAction('SUBSCRIBE/FAILURE_ADD_USER_OPTION');
export const successRemoveUserOption = createAction('SUBSCRIBE/SUCCESS_REMOVE_USER_OPTION');
export const failureRemoveUserOption = createAction('SUBSCRIBE/FAILURE_REMOVE_USER_OPTION');
export const resetUserOptions = createAction('SUBSCRIBE/RESET_USER_OPTIONS');
export const loadingUserOptions = createAction('SUBSCRIBE/LOADING_USER_OPTIONS');
export const successGetUserOptions = createAction('SUBSCRIBE/SUCCESS_GET_USER_OPTIONS');
export const failureGetUserOptions = createAction('SUBSCRIBE/FAILURE_GET_USER_OPTIONS');
export const loadingAvailableOptions = createAction('SUBSCRIBE/LOADING_AVAILABLE_OPTIONS');
export const successGetAvailableOptions = createAction('SUBSCRIBE/SUCCESS_GET_AVAILABLE_OPTIONS');
export const failureGetAvailableOptions = createAction('SUBSCRIBE/FAILURE_GET_AVAILABLE_OPTIONS');
export const setSelectedOptionId = createAction('SUBSCRIBE/SET_SELECTED_OPTION');
export const setSubscriptionPurchaseInProgress = createAction('SUBSCRIBE/SET_PURCHASE_IN_PROGRESS');
export const loadingUserInvoices = createAction('SUBSCRIBE/LOADING_USER_INVOICES');
export const setUserInvoices = createAction('SUBSCRIBE/SET_USER_INVOICES');
export const failureGetUserInvoices = createAction('SUBSCRIBE/FAILURE_GET_USER_INVOICES');
export const setPromoCode = createAction('SUBSCRIBE/SET_PROMO_CODE');
export const resetPromoCode = createAction('SUBSCRIBE/RESET_PROMO_CODE');

/**
 * Subscription: get all available packages
 * @param {Object} params
 * @param {Object} params.voucherCode - promo code
 * @return {Function}
 */
export function getAvailableOptions(params) {
  return (dispatch, getState) => {
    dispatch(loadingAvailableOptions());
    return SubscribeService.fetchAvailableOptions({
      languageId: getAlphaLangCode(getState()), ...params,
    })
      .then((options = []) => (
        dispatch(successGetAvailableOptions(options.sort((a, b) => b.priceWithoutVat - a.priceWithoutVat)))
      ))
      .catch((error) => {
        console.log('Error on get available options', error);
        dispatch(failureGetAvailableOptions(error));
      });
  };
}

/**
 * Subscription: check if promoCode is valid
 * @return {Function}
 */
export function checkPromoCodeValidity({ voucherCode }) {
  return (dispatch, getState) => SubscribeService.fetchAvailableOptions({
    languageId: getAlphaLangCode(getState()),
    voucherCode,
  })
    .then((options = []) => {
      if (options.length === 1) {
        dispatch(setPromoCode(voucherCode));
        dispatch(successGetAvailableOptions(options));
        dispatch(setSelectedOptionId(options[0].idOption));
        dispatch(push(RouterConstants.SUBSCRIBE_PROMOTION));
      } else {
        dispatch(resetPromoCode());
      }
    })
    .catch(() => dispatch(resetPromoCode()));
}

/**
 * Subscription: check has user active subscriptions
 * @return {Function}
 */
export function checkSubscriptionValidity(isProductAdded = false) {
  return (dispatch, getState) => {
    const { user: { authToken }, subscribe: { selectedOptionId } } = getState();

    dispatch(loadingUserOptions());
    return Promise.all([
      SubscribeService.checkOptionsValidity({
        productId: userProductId,
      }, authToken),
      SubscribeService.fetchOptionsHistory({
        languageId: getAlphaLangCode(getState()),
        productId: userProductId,
      }, authToken),
    ])
      .then(([optionsValidity, optionsHistory]) => {
        const isSubscriptionActive = getSubscriptionActiveStatus(optionsValidity);
        const userOptions = mapUserOptions(optionsValidity, optionsHistory);

        dispatch(successGetUserOptions({ isSubscriptionActive, userOptions }));
        if (!isSubscriptionActive && !selectedOptionId) {
          const userSelectedOption = userOptions
            .find(({ statusId }) => [
              subscriptionStatus.ORDERED,
            ].includes(statusId));

          if (userSelectedOption) {
            dispatch(setSelectedOptionId(userSelectedOption.optionId));
          }
        }
      })
      .catch((error) => {
        if (!isProductAdded && error && (error.code === -2333 || error.code === -2332)) {
          console.error('error - user has no added product ', error);
          SubscribeService.productsAdd({ productIds: [userProductId] }, authToken)
            .then(() => dispatch(checkSubscriptionValidity(true)))
            .catch((error) => {
              const code = get(error, 'code', null);
              dispatch(failureGetUserOptions(error));
              console.error('error no product add', error);
              if (code === -997) {
                dispatch(userLogout());
                dispatch(push(RouterConstants.SIGN_IN));
                dispatch(notifyUserNotSupported(true));
              }
            });
        } else {
          console.error('error on check subscription validity ', error);
          dispatch(failureGetUserOptions());
        }
      });
  };
}

/**
 * Subscription: prepare selected option to purchase
 * @return {Function}
 */
export function subscribeSelectedOption() {
  return (dispatch, getState) => {
    dispatch(setSubscriptionPurchaseInProgress(true));

    return dispatch(checkSubscriptionValidity())
      .then(() => dispatch(removeOldOptions()))
      .then(() => {
        const { subscribe: { userOptions, selectedOptionId } } = getState();
        const hasSelectedOption = userOptions.find(({ optionId }) => optionId === selectedOptionId);

        if (!hasSelectedOption) {
          return dispatch(addOptionToUserSubscriptions(selectedOptionId));
        }
      })
      .finally(() => {
        const { subscribe: { error } } = getState();
        if (error && error.code) {
          dispatch(push(RouterConstants.SUBSCRIBE));
        } else {
          return dispatch(processPayment());
        }
      })
      .then(() => {
        /**
          Please note: we need add timeout because if redirection happens (in processPayment),
         browser needs some time to redirect and without timeout the loader is hidden before redirection happened
        */
        setTimeout(() => {
          dispatch(setSubscriptionPurchaseInProgress(false));
        }, 1500);
      });
  };
}

function removeOldOptions() {
  return (dispatch, getState) => {
    const { subscribe: { userOptions, selectedOptionId } } = getState();

    const removeAddedSubscriptions = userOptions
      .reduce((removeAddedSubscriptions, { optionId, saleOptionId, statusId }) => {
        if (statusId === subscriptionStatus.ORDERED && optionId !== selectedOptionId) {
          removeAddedSubscriptions.push(dispatch(removeOptionFromUserSubscriptions(optionId, saleOptionId)));
        }

        return removeAddedSubscriptions;
      }, []);

    return Promise.all(removeAddedSubscriptions);
  };
}

/**
 * Subscription: add option to user subscriptions
 * @return {Function}
 */
export function addOptionToUserSubscriptions(optionId) {
  return async (dispatch, getState) => {
    const { user: { authToken } } = getState();
    const params = { optionIds: [optionId], userProductId };

    return SubscribeService.optionsAdd(params, authToken)
      .then(() => dispatch(successAddUserOption()))
      .catch((error) => {
        console.log('Error on add option to user subscriptions', error);
        dispatch(failureAddUserOption({ error }));
      });
  };
}

/**
 * Subscription: remove option from user subscriptions
 * @return {Function}
 */
export function removeOptionFromUserSubscriptions(optionId, saleOptionId) {
  return (dispatch, getState) => {
    const { user: { authToken } } = getState();
    const params = {
      productId: userProductId,
      optionIds: [optionId],
      saleOptionId,
      idReason: 1,
    };

    return SubscribeService.optionsRemove(params, authToken)
      .then(() => dispatch(successRemoveUserOption()))
      .catch((error) => {
        console.error('error on remove option from user subscriptions', error);
        dispatch(failureRemoveUserOption({ error }));
      });
  };
}

/**
 * Subscription: enable auto-renewal
 * @return {Function}
 */
export function enableAutoRenewSubscription(optionId) {
  return (dispatch) => {
    dispatch(setSubscriptionPurchaseInProgress(true));
    dispatch(addOptionToUserSubscriptions(optionId))
      .then(() => dispatch(checkSubscriptionValidity()))
      .finally(() => dispatch(setSubscriptionPurchaseInProgress(false)));
  };
}

/**
 * Subscription: disable auto-renewal
 * @return {Function}
 */
export function disableAutoRenewSubscription(optionId, saleOptionId) {
  return (dispatch) => {
    dispatch(setSubscriptionPurchaseInProgress(true));
    dispatch(removeOptionFromUserSubscriptions(optionId, saleOptionId))
      .then(() => dispatch(checkSubscriptionValidity()))
      .finally(() => dispatch(setSubscriptionPurchaseInProgress(false)));
  };
}

export function getInvoicesList(params) {
  return (dispatch, getState) => {
    const state = getState();
    const { user: { authToken } } = state;
    dispatch(loadingUserInvoices());

    return invoicesList({ ...params, languageId: getAlphaLangCode(getState()) }, authToken)
      .then((invoices) => {
        dispatch(setUserInvoices(invoices));
      })
      .catch((error) => {
        console.error('Error on getting account invoices: ', error);
        dispatch(failureGetUserInvoices(error));
      });
  };
}
